import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ROLES } from "../utils/roles";
import { ADMIN_API_BASE_URL } from "../utils/api";
import apiService from "../services/apiService";

export default function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<{
    username?: string;
    password?: string;
  }>({});
  const navigate = useNavigate();
  const { login } = useAuth();

  // ✅ Load saved credentials if "Remember Me" was checked previously
  useEffect(() => {
    const savedUsername = localStorage.getItem("savedUsername");
    const savedRemember = localStorage.getItem("rememberMe") === "true";
    if (savedUsername && savedRemember) {
      setUsername(savedUsername);
      setRememberMe(true);
    }
  }, []);
  const validateInputs = () => {
    const errors: { username?: string; password?: string } = {};

    if (!username.trim()) {
      errors.username = "Username is required";
    } else if (username.length < 3) {
      errors.username = "Username must be at least 3 characters";
    }

    if (!password.trim()) {
      errors.password = "Password is required";
    } else if (password.length < 6) {
      errors.password = "Password must be at least 6 characters";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleLogin = async () => {
    if (!validateInputs()) return;

    setLoading(true);
    setError(null);

  // 🧹 Trim spaces before sending to API
  const cleanUsername = username.trim();
  const cleanPassword = password.trim();

  try {
    const res: any = await apiService.post("/admin-login", {
      username: cleanUsername,
      password: cleanPassword,
    });


      console.log("Login response:", res);
      // Axios already gives you parsed data directly
      const data =res?.data;

      const token =  data?.token;
      const role =
        data?.role || (username === "super" ? ROLES.SUPER_ADMIN : ROLES.ADMIN);

      if (!res?.status) {
        throw new Error("Invalid response: missing token");
      }

      // ✅ Save 'Remember Me' preferences
      if (rememberMe) {
        localStorage.setItem("rememberMe", "true");
        localStorage.setItem("savedUsername", username);
      } else {
        localStorage.removeItem("rememberMe");
        localStorage.removeItem("savedUsername");
      }

      // ✅ Update auth context
      login(token, role, rememberMe);

      // ✅ Redirect to dashboard
      navigate("/events");
    } catch (error: any) {
      // Axios errors have response data
      console.log("hfgdjs", error)
      const message =
        error?.response?.data?.data?.message ||
        error?.data?.error ||
        error.message ||
        "Login failed. Please try again.";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const isFormValid =
    username.trim().length >= 3 && password.trim().length >= 6 && !loading;

  return (
    <div className="flex justify-center items-center h-screen bg-gray-100">
      <div className="p-6 bg-white rounded shadow-md w-96">
        <h1 className="text-xl font-bold mb-4">Admin Login</h1>

        <div className="mb-3">
          <input
            id="username"
            name="username"
            type="text"
            placeholder="Username"
            value={username}
            onChange={(e) => {
              setUsername(e.target.value);
              if (formErrors.username) {
                setFormErrors((prev) => ({ ...prev, username: undefined }));
              }
            }}
            className={`border w-full p-2 rounded ${
              formErrors.username ? "border-red-500" : "border-gray-300"
            }`}
          />
          {formErrors.username && (
            <p className="text-red-500 text-sm mt-1">{formErrors.username}</p>
          )}
        </div>

        <div className="mb-2">
          <input
            id="password"
            name="password"
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (formErrors.password) {
                setFormErrors((prev) => ({ ...prev, password: undefined }));
              }
            }}
            className={`border w-full p-2 rounded ${
              formErrors.password ? "border-red-500" : "border-gray-300"
            }`}
          />
          {formErrors.password && (
            <p className="text-red-500 text-sm mt-1">{formErrors.password}</p>
          )}
        </div>

        {/* ✅ Remember Me */}
        <div className="flex items-center justify-between mb-4">
          <label className="flex items-center space-x-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
            />
            <span>Remember me</span>
          </label>
        </div>

        {error && <p className="text-red-500 text-sm mb-3">{error}</p>}

        <button
          onClick={handleLogin}
          disabled={!isFormValid}
          className={`w-full bg-blue-500 hover:bg-blue-600 text-white p-2 rounded transition ${
            !isFormValid ? "opacity-60 cursor-not-allowed" : ""
          }`}
        >
          {loading ? "Logging in..." : "Login"}
        </button>
      </div>
    </div>
  );
}
